import { newInstance, EVENT_CONNECTION_DBL_CLICK, CONNECTION, EVENT_DRAG_STOP } from "@jsplumb/browser-ui";
import { FlowchartConnector } from "@jsplumb/connector-flowchart";

// 流程图组件
export default class Flow {
    nodeTypes = {}; // 节点类型数据
    nodes = []; // 节点实例数据
    instance = null; // jsPlumb instance
    container = ""; // jsplumb container dom id

    constructor({ container, connectorDbClick, connectOn, draggerEnd }) {
        this.container = container;
        this.instance = newInstance({
            container: document.querySelector(this.container),
            reattachConnections: true,
            connector: {
                type: FlowchartConnector.type,
                options: {
                    cornerRadius: 5,
                    stub: [10, 10],
                    alwaysRespectStubs: true,
                }
            },
            paintStyle: { strokeWidth: 1, stroke: "#3fbdff" },
            hoverPaintStyle: {
                stroke: "#3fbdff",
                strokeWidth: 3,
            },
            connectionOverlays: [{ type: "Arrow", options: { width: 8, length: 8, location: 1, paintStyle: { stroke: '#3fbdff', fill: '#3fbdff' } } }],
            endpointStyle: {
                fill: "#aaaaaa20",
                stroke: "#aaa",
                strokeWidth: 1
            },
            endpointHoverStyle: {
                strokeWidth: 3,
                stroke: "#3fbdff",
            },
            dragOptions: {
                containment: "notNegative"
            }
        });
        // 连线双击事件
        this.instance.bind(EVENT_CONNECTION_DBL_CLICK, (connector) => {
            connectorDbClick && connectorDbClick(connector);
        });
        // 连线连接事件
        this.instance.bind(CONNECTION, (connector) => {
            connectOn && connectOn(connector);
        });
        // 节点拖动事件
        this.instance.bind(EVENT_DRAG_STOP, ({ elements = [] }) => {
            elements.forEach(elem => {
                const node = this.nodes.find(n => elem.id === n.id);
                if (node) {
                    const movedMore = Math.abs((node.position.x || 0) - elem.pos.x) > 5 || Math.abs((node.position.y || 0) - elem.pos.y) > 5;
                    if (!movedMore) {
                        return;
                    }
                    node.position = elem.pos;
                    draggerEnd && draggerEnd(node);
                }
            });
        });
    }

    // 初始化节点和连线
    async init(nodes = [], connectors = []) {
        this.instance.setSuspendDrawing(true);
        for (const n of nodes) {
            await this.addNode(n);
        }
        connectors.forEach(c => this.connectLine(c[0], c[1], { fireEvent: false }));
        this.instance.setSuspendDrawing(false, true);
    }

    // 重置
    reset() {
        this.instance.reset();
        this.nodes.splice(0);
    }

    // 销毁
    destroy() {
        this.instance.destroy();
        this.nodes.splice(0);
        this.instance = null;
    }

    // 更新节点和连线
    async updateAll(nodes = [], connectors = []) {
        this.instance.setSuspendDrawing(true);

        this.deleteConnections(connectors);
        await this.updateAllNodes(nodes);
        this.addConnectionsIfNotExist(connectors);

        this.instance.setSuspendDrawing(false, true);
    }

    // 注册节点类型
    injectNodeType(type, nodeType) {
        this.nodeTypes[type] = nodeType;
    }

    // endpoint 默认参数
    getEndpointDefParam({ source }) {
        if (source) {
            return { endpoint: { type: "Rectangle", options: { width: 20, height: 14 } } };
        }
        return { endpoint: { type: "Rectangle", options: { width: 10, height: 8 } } };
    }

    // 添加endpoints
    addNodeEndpoints(nodeType, node) {
        const endpoints = nodeType.endpoints(node) || [];
        endpoints.forEach((ep) => {
            const uuid = ep.elemId || node.id;
            const elem = document.getElementById(uuid);
            this.instance.addEndpoint(elem, { ...this.getEndpointDefParam(ep), ...ep, uuid });
        });
    }

    // 更新endpoints
    updateNodeEndpoints(nodeType, node) {
        const oldEndPoints = this.instance.getEndpoints(document.getElementById(node.id));
        const endpoints = nodeType.endpoints(node) || [];
        endpoints.forEach(ep => {
            const uuid = ep.elemId || node.id;
            const oldIndex = oldEndPoints.findIndex(oep => oep.uuid === uuid);
            if (oldIndex !== -1) {
                oldEndPoints.splice(oldIndex, 1);
                return;
            }
            const elem = document.getElementById(uuid);
            this.instance.addEndpoint(elem, { ...this.getEndpointDefParam(ep), ...ep, uuid });
        });
        oldEndPoints.forEach(oep => {
            this.instance.deleteEndpoint(oep);
        });
    }

    // 删除endpoints
    deleteNodeEndpoints(nodeType, node) {
        const endpoints = nodeType.endpoints(node) || [];
        endpoints.forEach(ep => {
            const endPoints = this.instance.getEndpoints(document.getElementById(ep.elemId || node.id));
            endPoints.forEach(oep => {
                this.instance.deleteEndpoint(oep);
            });
        });
    }

    // 添加节点
    async addNode(data) {
        const nodeType = this.nodeTypes[data.nodeType];
        const nodeData = { ...data, component: nodeType.component };
        this.nodes.push(nodeData);
        await nodeType.render(nodeData);
        this.addNodeEndpoints(nodeType, nodeData);
    }

    // 更新节点
    async updateNode(data) {
        const nodeType = this.nodeTypes[data.nodeType];
        const index = this.nodes.findIndex(n => n.id === data.id);
        this.deleteNodeEndpoints(nodeType, this.nodes[index]);
        const newNode = { ...this.nodes[index], ...data };
        this.nodes.splice(index, 1, newNode);
        await nodeType.update(newNode);
        this.addNodeEndpoints(nodeType, newNode);
    }

    // 删除节点
    delNode({ id }) {
        const index = this.nodes.findIndex(n => n.id === id);
        if (index === -1) {
            return;
        }
        const node = this.nodes[index];
        const nodeType = this.nodeTypes[node.nodeType];
        this.deleteNodeEndpoints(nodeType, node);
        nodeType.remove(node);
        this.nodes.splice(index, 1);
    }

    // 更新所有节点
    async updateAllNodes(nodes) {
        const oldNodes = [...this.nodes];
        for (const n of nodes) {
            const oldIndex = oldNodes.findIndex(on => on.id === n.id);
            if (oldIndex !== -1) {
                await this.updateNode(n);
                oldNodes.splice(oldIndex, 1);
                continue;
            }
            await this.addNode(n);
        }
        if (oldNodes.length) {
            for (const n of oldNodes) {
                this.delNode(n);
            }
        }
    }

    // 连接节点
    connectLine(sourceElemId, targetElemId, param) {
        const elemId1 = document.getElementById(sourceElemId);
        const elemId2 = document.getElementById(targetElemId);
        const source = this.instance.getEndpoints(elemId1)[0];
        const target = this.instance.getEndpoints(elemId2)[0];

        this.instance.connect({ source, target }, param);
    }

    // 删除连线
    delConnector(source) {
        this.instance.select({ source: source.id }).deleteAll();
    }

    // 删除连线
    deleteConnection(connector, param) {
        this.instance.deleteConnection(connector, param);
    }

    // 添加连线
    addConnectionsIfNotExist(connectors) {
        const oldConnectors = this.instance.getConnections();
        connectors.forEach(([sourceId, targetId]) => {
            const oldIndex = oldConnectors.findIndex(oldCon => oldCon.sourceId === sourceId && oldCon.targetId === targetId);
            if (oldIndex !== -1) {
                oldConnectors.splice(oldIndex, 1);
                return;
            }
            this.connectLine(sourceId, targetId, { fireEvent: false });
        });
    }

    // 删除指定连线
    deleteConnectionsIfExist(connectors) {
        const oldConnectors = this.instance.getConnections();
        connectors.forEach(conn => {
            const oldIndex = oldConnectors.findIndex(oldCon => oldCon.sourceId === conn.sourceId && oldCon.targetId === conn.targetId);
            if (oldIndex !== -1) {
                this.deleteConnection(conn, { fireEvent: false });
                oldConnectors.splice(oldIndex, 1);
                return;
            }
        });
    }

    // 删除所有连线
    deleteConnections() {
        const oldConnectors = this.instance.getConnections();
        oldConnectors.forEach(conn => {
            this.deleteConnection(conn, { fireEvent: false });
        });
    }

    // 获取nodeTypes列表
    getNodeTypeList() {
        const keys = Object.keys(this.nodeTypes);
        return keys.map(key => ({
            nodeType: key,
            ...this.nodeTypes[key]
        }));
    }

    // 获取流程图的节点和连线数据
    getFlowData() {
        const connectors = this.instance.getConnections().map(c => [c.sourceId, c.targetId]);
        const nodes = this.nodes;
        return { connectors, nodes };
    }

    // 缩放功能
    setZoom(newRate) {
        const rpaContainEle = document.querySelector(this.container);
        const { offsetWidth, offsetHeight } = rpaContainEle;
        rpaContainEle.style.transform = `scale(${newRate})`;
        rpaContainEle.style.marginLeft = `${(offsetWidth * (newRate - 1)) / 2}px`;
        rpaContainEle.style.marginTop = `${(offsetHeight * (newRate - 1)) / 2}px`;
        this.instance.setZoom(newRate);
    }
}
